/**
 * CL-PRODUCT-SELECTIONS-002 — Canonical Products & Selections collection paths (pure).
 *
 * Global templates are copy-only references — not live inherited schemas.
 * No public URL construction.
 */

export const PM_PROJECTS_COLLECTION = "pmProjects";

export const PROJECT_PRODUCT_CATEGORIES_SUBCOLLECTION = "projectProductCategories";
export const PROJECT_PRODUCT_AREAS_SUBCOLLECTION = "projectProductAreas";
export const PROJECT_PRODUCT_REQUIREMENTS_SUBCOLLECTION = "projectProductRequirements";
export const PROJECT_PRODUCT_OPTIONS_SUBCOLLECTION = "options";
export const PROJECT_PRODUCT_EVENTS_SUBCOLLECTION = "events";
export const PROJECT_PRODUCT_SUBMISSIONS_SUBCOLLECTION = "submissions";
export const PROJECT_PRODUCT_CLIENT_OPTION_DECISIONS_SUBCOLLECTION = "clientOptionDecisions";
export const PROJECT_PRODUCT_CLIENT_PUBLICATIONS_SUBCOLLECTION = "projectProductClientPublications";
export const PROJECT_PRODUCT_CLIENT_FILE_ENTITLEMENTS_SUBCOLLECTION = "projectProductClientFileEntitlements";
export const PROJECT_PRODUCT_CLIENT_FILE_ACCESS_SUBCOLLECTION = "projectProductClientFileAccess";

export const PROJECT_PRODUCT_CATEGORY_TEMPLATES_COLLECTION = "projectProductCategoryTemplates";

function safeStr(value) {
  return String(value ?? "").trim();
}

/**
 * Reject missing, whitespace-only, or slash-containing Firestore path segment IDs.
 */
export function isValidProjectProductPathId(value) {
  const id = safeStr(value);
  return id.length > 0 && !id.includes("/");
}

export function assertValidProjectProductPathId(value, label = "id") {
  if (!isValidProjectProductPathId(value)) {
    throw new Error(`Invalid ${label}.`);
  }
  return safeStr(value);
}

export function projectProductCategoriesCollectionPath(projectId) {
  const pid = assertValidProjectProductPathId(projectId, "projectId");
  return `${PM_PROJECTS_COLLECTION}/${pid}/${PROJECT_PRODUCT_CATEGORIES_SUBCOLLECTION}`;
}

export function projectProductCategoryDocPath(projectId, categoryId) {
  return `${projectProductCategoriesCollectionPath(projectId)}/${assertValidProjectProductPathId(categoryId, "categoryId")}`;
}

export function projectProductAreasCollectionPath(projectId) {
  const pid = assertValidProjectProductPathId(projectId, "projectId");
  return `${PM_PROJECTS_COLLECTION}/${pid}/${PROJECT_PRODUCT_AREAS_SUBCOLLECTION}`;
}

export function projectProductAreaDocPath(projectId, areaId) {
  return `${projectProductAreasCollectionPath(projectId)}/${assertValidProjectProductPathId(areaId, "areaId")}`;
}

export function projectProductRequirementsCollectionPath(projectId) {
  const pid = assertValidProjectProductPathId(projectId, "projectId");
  return `${PM_PROJECTS_COLLECTION}/${pid}/${PROJECT_PRODUCT_REQUIREMENTS_SUBCOLLECTION}`;
}

export function projectProductRequirementDocPath(projectId, requirementId) {
  return `${projectProductRequirementsCollectionPath(projectId)}/${assertValidProjectProductPathId(requirementId, "requirementId")}`;
}

export function projectProductOptionsCollectionPath(projectId, requirementId) {
  return `${projectProductRequirementDocPath(projectId, requirementId)}/${PROJECT_PRODUCT_OPTIONS_SUBCOLLECTION}`;
}

export function projectProductOptionDocPath(projectId, requirementId, optionId) {
  return `${projectProductOptionsCollectionPath(projectId, requirementId)}/${assertValidProjectProductPathId(optionId, "optionId")}`;
}

export function projectProductEventsCollectionPath(projectId, requirementId) {
  return `${projectProductRequirementDocPath(projectId, requirementId)}/${PROJECT_PRODUCT_EVENTS_SUBCOLLECTION}`;
}

export function projectProductEventDocPath(projectId, requirementId, eventId) {
  return `${projectProductEventsCollectionPath(projectId, requirementId)}/${assertValidProjectProductPathId(eventId, "eventId")}`;
}

export function projectProductSubmissionsCollectionPath(projectId, requirementId) {
  return `${projectProductRequirementDocPath(projectId, requirementId)}/${PROJECT_PRODUCT_SUBMISSIONS_SUBCOLLECTION}`;
}

export function projectProductSubmissionDocPath(projectId, requirementId, submissionId) {
  return `${projectProductSubmissionsCollectionPath(projectId, requirementId)}/${assertValidProjectProductPathId(submissionId, "submissionId")}`;
}

export function projectProductClientOptionDecisionsCollectionPath(projectId, requirementId) {
  return `${projectProductRequirementDocPath(projectId, requirementId)}/${PROJECT_PRODUCT_CLIENT_OPTION_DECISIONS_SUBCOLLECTION}`;
}

export function projectProductClientOptionDecisionDocPath(projectId, requirementId, decisionId) {
  return `${projectProductClientOptionDecisionsCollectionPath(projectId, requirementId)}/${assertValidProjectProductPathId(decisionId, "decisionId")}`;
}

export function projectProductCategoryTemplateDocPath(templateId) {
  return `${PROJECT_PRODUCT_CATEGORY_TEMPLATES_COLLECTION}/${assertValidProjectProductPathId(templateId, "templateId")}`;
}

export function projectProductClientPublicationsCollectionPath(projectId) {
  const pid = assertValidProjectProductPathId(projectId, "projectId");
  return `${PM_PROJECTS_COLLECTION}/${pid}/${PROJECT_PRODUCT_CLIENT_PUBLICATIONS_SUBCOLLECTION}`;
}

export function projectProductClientPublicationDocPath(projectId, requirementId) {
  return `${projectProductClientPublicationsCollectionPath(projectId)}/${assertValidProjectProductPathId(requirementId, "requirementId")}`;
}

export function projectProductClientPublicationOptionsCollectionPath(projectId, requirementId) {
  return `${projectProductClientPublicationDocPath(projectId, requirementId)}/${PROJECT_PRODUCT_OPTIONS_SUBCOLLECTION}`;
}

export function projectProductClientPublicationOptionDocPath(projectId, requirementId, optionId) {
  return `${projectProductClientPublicationOptionsCollectionPath(projectId, requirementId)}/${assertValidProjectProductPathId(optionId, "optionId")}`;
}

export function projectProductClientFileEntitlementsCollectionPath(projectId) {
  const pid = assertValidProjectProductPathId(projectId, "projectId");
  return `${PM_PROJECTS_COLLECTION}/${pid}/${PROJECT_PRODUCT_CLIENT_FILE_ENTITLEMENTS_SUBCOLLECTION}`;
}

export function projectProductClientFileEntitlementDocPath(projectId, fileId, requirementId) {
  const entitlementId = `${assertValidProjectProductPathId(fileId, "fileId")}__${assertValidProjectProductPathId(requirementId, "requirementId")}`;
  return `${projectProductClientFileEntitlementsCollectionPath(projectId)}/${entitlementId}`;
}

export function projectProductClientFileAccessCollectionPath(projectId) {
  const pid = assertValidProjectProductPathId(projectId, "projectId");
  return `${PM_PROJECTS_COLLECTION}/${pid}/${PROJECT_PRODUCT_CLIENT_FILE_ACCESS_SUBCOLLECTION}`;
}

export function projectProductClientFileAccessDocPath(projectId, fileId) {
  return `${projectProductClientFileAccessCollectionPath(projectId)}/${assertValidProjectProductPathId(fileId, "fileId")}`;
}
